import React, { useState, useEffect, useCallback } from 'react';
import { FiFileText, FiFilter, FiDownload, FiInfo, FiAlertCircle, FiCheckCircle, FiHelpCircle } from 'react-icons/fi';
import { fetchRuns } from '../services/api';

const LogAnalytics = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logFilter, setLogFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSourceHelp, setShowSourceHelp] = useState(false);

  const loadLogs = async () => {
    try {
      const runsResponse = await fetchRuns();
      // Handle both cases: when fetchRuns returns an array directly or an object with runs property
      const runs = Array.isArray(runsResponse) ? runsResponse : (runsResponse?.runs || []);

      // Extract all logs from all runs
      const allLogs = [];
      runs.forEach((run, runIndex) => {
        if (run.logs && Array.isArray(run.logs)) {
          run.logs.forEach(log => {
            allLogs.push({
              ...log,
              runId: run.run_id,
              runIndex: runIndex + 1,
              projectId: run.project_id,
              projectName: run.project_name || 'Unknown Project'
            });
          });
        }
      });

      // Sort logs by timestamp (most recent first)
      allLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setLogs(allLogs);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = useCallback(() => {
    let filtered = [...logs];

    // Filter by log type
    if (logFilter !== 'all') {
      filtered = filtered.filter(log => log.type === logFilter);
    }

    // Filter by source
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(log => log.source === sourceFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.source.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredLogs(filtered);
  }, [logs, logFilter, sourceFilter, searchTerm]);

  useEffect(() => {
    loadLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, logFilter, sourceFilter, searchTerm, filterLogs]);

  const getLogStats = () => {
    const stats = {
      total: logs.length,
      info: logs.filter(log => log.type === 'info').length,
      warning: logs.filter(log => log.type === 'warning').length,
      error: logs.filter(log => log.type === 'error').length
    };
    return stats;
  };

  const getSources = () => {
    // Get actual sources from logs
    const actualSources = [...new Set(logs.map(log => log.source))];
    
    // Define comprehensive list of log sources
    const additionalSources = [
      'API Gateway',
      'Client App',
      'Backend Service',
      'Database',
      'Authentication',
      'File System',
      'Network',
      'Cache',
      'Background Jobs',
      'External Services',
      'Security',
      'Performance Monitor',
      'Load Balancer',
      'Message Queue',
      'Webhook Handler',
      'Scheduler',
      'Data Pipeline',
      'Monitoring Agent',
      'Configuration Manager',
      'Session Manager'
    ];
    
    // Combine actual sources with additional ones, removing duplicates
    const allSources = [...new Set([...actualSources, ...additionalSources])];
    
    // Sort sources alphabetically
    return allSources.sort();
  };

  const getSourceDescription = (source) => {
    const descriptions = {
      'Project Manager': 'Logs from project management operations and workflow execution',
      'API Gateway': 'Logs from API gateway requests, routing, and authentication',
      'Client App': 'Frontend application logs, user interactions, and client-side errors',
      'Backend Service': 'Core business logic, API endpoints, and service operations',
      'Database': 'Database connection, query execution, and transaction logs',
      'Authentication': 'User authentication, authorization, and session management logs',
      'File System': 'File operations, uploads, downloads, and storage management',
      'Network': 'Network connectivity, API calls, and external service communication',
      'Cache': 'Cache operations, hits, misses, and eviction events',
      'Background Jobs': 'Scheduled tasks, queue processing, and background operations',
      'External Services': 'Third-party service integrations and API calls',
      'Security': 'Security events, access attempts, and threat detection',
      'Performance Monitor': 'System performance metrics and resource utilization',
      'Load Balancer': 'Traffic distribution, health checks, and routing decisions',
      'Message Queue': 'Message processing, queue management, and event handling',
      'Webhook Handler': 'Incoming webhook processing and external integrations',
      'Scheduler': 'Cron jobs, task scheduling, and automated processes',
      'Data Pipeline': 'ETL processes, data transformations, and analytics workflows',
      'Monitoring Agent': 'System health monitoring and alert generation',
      'Configuration Manager': 'Application configuration changes and environment management',
      'Session Manager': 'User session handling, token management, and state tracking'
    };
    
    return descriptions[source] || 'General application logs';
  };

  const getLogIcon = (type) => {
    switch (type) {
      case 'info':
        return <FiInfo className="text-blue-500" />;
      case 'warning':
        return <FiAlertCircle className="text-yellow-500" />;
      case 'error':
        return <FiAlertCircle className="text-red-500" />;
      default:
        return <FiCheckCircle className="text-green-500" />;
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'info':
        return 'text-blue-600 bg-blue-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-green-600 bg-green-50';
    }
  };

  const exportLogs = () => {
    const dataStr = JSON.stringify(filteredLogs, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `logs_export_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Don't render if no logs
  if (!loading && logs.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="text-center py-8">
          <FiFileText className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Logs Available</h3>
          <p className="text-gray-500">Log analytics will appear here once log data is available.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const stats = getLogStats();
  const sources = getSources();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <FiFileText className="w-8 h-8 text-indigo-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Log Analytics</h2>
              <p className="text-gray-600">Monitor and analyze system logs across different sources</p>
            </div>
          </div>
          <button
            onClick={exportLogs}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <FiDownload className="mr-2" />
            Export Logs
          </button>
        </div>

        {/* Log Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Logs</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Info Logs</p>
            <p className="text-2xl font-bold text-blue-600">{stats.info}</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Warnings</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.warning}</p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Errors</p>
            <p className="text-2xl font-bold text-red-600">{stats.error}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center space-x-2">
            <FiFilter className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          <select
            value={logFilter}
            onChange={(e) => setLogFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Types</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>

          <div className="relative">
            <div className="flex items-center space-x-2">
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-8"
            >
              <option value="all">All Sources</option>
              {sources.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
            <button
              onClick={() => setShowSourceHelp(!showSourceHelp)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Learn about log sources and filtering"
            >
              <FiHelpCircle size={16} />
            </button>
            </div>
            {sourceFilter !== 'all' && (
              <div className="mt-2 text-xs text-gray-600 bg-blue-50 p-2 rounded border border-blue-200">
                <strong>Active Filter:</strong> {sourceFilter} - {getSourceDescription(sourceFilter)}
              </div>
            )}
          </div>

          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 max-w-md"
          />
        </div>

        {/* Source Help Tooltip */}
        {showSourceHelp && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex justify-between items-start mb-3">
              <h4 className="text-sm font-semibold text-blue-900">📊 Log Sources & Filtering Guide</h4>
              <button
                onClick={() => setShowSourceHelp(false)}
                className="text-blue-600 hover:text-blue-800"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-blue-800 mb-3">
              <strong>Source filtering</strong> allows you to focus on specific system components. This helps with:
            </p>
            <ul className="text-sm text-blue-800 space-y-1 mb-4">
              <li>• <strong>🔍 Targeted Debugging:</strong> Isolate issues to specific system components</li>
              <li>• <strong>Performance Analysis:</strong> Identify bottlenecks in particular services</li>
              <li>• <strong>📈 Usage Patterns:</strong> Understand component utilization and load distribution</li>
              <li>• <strong>🛡️ Security Monitoring:</strong> Track security events from specific sources</li>
              <li>• <strong>🔄 Workflow Analysis:</strong> Trace requests across different system layers</li>
            </ul>
            
            <div className="bg-white p-3 rounded border border-blue-300">
              <h5 className="text-xs font-semibold text-blue-900 mb-2">📋 Available Log Sources:</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-blue-800">
                {sources.map(source => (
                  <div key={source} className="flex items-start space-x-2 p-1 hover:bg-blue-50 rounded">
                    <span className="font-medium text-blue-900 min-w-[120px]">{source}</span>
                    <span className="text-blue-700">{getSourceDescription(source)}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-xs text-yellow-800">
                <strong>💡 Tip:</strong> Combine source filters with log type filters for more precise analysis. 
                Use the search function to find specific error messages or patterns.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Log Timeline */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Log Timeline</h3>
          {(sourceFilter !== 'all' || logFilter !== 'all' || searchTerm) && (
            <div className="flex items-center space-x-2">
              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                Filtered View
              </span>
              <span className="text-xs text-gray-500">
                {filteredLogs.length} of {logs.length} logs
              </span>
            </div>
          )}
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredLogs.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No logs found matching your filters</p>
          ) : (
            filteredLogs.map((log, index) => (
              <div 
                key={index} 
                className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="mt-1">{getLogIcon(log.type)}</div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`text-xs px-2 py-1 rounded-full ${getLogColor(log.type)}`}>
                      {log.type.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-500">•</span>
                    <span className="text-xs text-gray-500 font-medium">{log.source}</span>
                    <span className="text-xs text-gray-500">•</span>
                    <span className="text-xs text-blue-600 font-medium">{log.projectName}</span>
                    <span className="text-xs text-gray-500">•</span>
                    <span className="text-xs text-gray-500">Run #{log.runIndex}</span>
                  </div>
                  <p className="text-sm text-gray-800">{log.message}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Log Sources Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Logs by Source</h3>
            {sourceFilter !== 'all' && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Filtered: {sourceFilter}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {sources.map(source => {
              const count = logs.filter(log => log.source === source).length;
              const filteredCount = sourceFilter === 'all' ? count : 
                (sourceFilter === source ? count : 0);
              const totalForPercentage = sourceFilter === 'all' ? logs.length : 
                logs.filter(log => log.source === sourceFilter).length;
              const percentage = totalForPercentage > 0 ? (filteredCount / totalForPercentage * 100).toFixed(1) : 0;
              
              return (
                <div key={source} className={`flex items-center justify-between p-2 rounded ${
                  sourceFilter === source ? 'bg-blue-50 border border-blue-200' : ''
                }`}>
                  <span className={`text-sm ${sourceFilter === source ? 'font-semibold text-blue-900' : 'text-gray-700'}`}>
                    {source}
                  </span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${sourceFilter === source ? 'bg-blue-600' : 'bg-indigo-600'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className={`text-sm w-12 text-right ${
                      sourceFilter === source ? 'text-blue-600 font-semibold' : 'text-gray-500'
                    }`}>
                      {percentage}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Recent Log Summary</h3>
            {sourceFilter !== 'all' && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                {filteredLogs.length} logs
              </span>
            )}
          </div>
          <div className="space-y-3">
            <div className="text-sm text-gray-600">
              <p className="mb-2">
                <strong>Last log entry:</strong> {filteredLogs[0] ? new Date(filteredLogs[0].timestamp).toLocaleString() : 'N/A'}
              </p>
              <p className="mb-2">
                <strong>Most active source:</strong> {
                  sourceFilter === 'all' 
                    ? (() => {
                        const sourceCounts = {};
                        logs.forEach(log => {
                          sourceCounts[log.source] = (sourceCounts[log.source] || 0) + 1;
                        });
                        const mostActive = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1])[0];
                        return mostActive ? mostActive[0] : 'N/A';
                      })()
                    : sourceFilter
                }
              </p>
              <p className="mb-2">
                <strong>Total runs analyzed:</strong> {[...new Set(filteredLogs.map(log => log.runId))].length}
              </p>
              <p className="mb-2">
                <strong>Log types in filtered data:</strong> {
                  [...new Set(filteredLogs.map(log => log.type))].join(', ') || 'None'
                }
              </p>
              {sourceFilter !== 'all' && (
                <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs text-blue-800">
                    <strong>Filtered Analysis:</strong> Showing logs from <strong>{sourceFilter}</strong> only. 
                    This helps focus on specific system component behavior.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogAnalytics; 